import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Users, Moon, Brain, Eye } from 'lucide-react';
import './Services.css';

export const Services = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const services = [
    {
      icon: <Heart size={32} />,
      title: 'Healing & Embodiment Sessions',
      description: 'Individual sessions focused on reconnecting with your body as a sacred vessel of truth and wisdom.',
      features: [
        'Somatic healing practices',
        'Body-centered therapy',
        'Trauma release work',
        'Embodiment coaching'
      ]
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Workshops & Ceremonies',
      description: 'Transformative group experiences designed to create deep connection and collective healing.',
      features: [
        'Women\'s circles',
        'Sacred ceremonies',
        'Group healing sessions',
        'Community gatherings'
      ]
    },
    {
      icon: <Brain size={32} />,
      title: 'Spiritual Psychology Mentorship',
      description: 'In-depth guidance combining psychological depth with spiritual wisdom for lasting transformation.',
      features: [
        'Shadow work exploration',
        'Inner child healing',
        'Spiritual integration',
        'Personal transformation'
      ]
    },
    {
      icon: <Users size={32} />,
      title: 'Sacred Union & Relationship Work',
      description: 'Guidance for couples and individuals seeking deeper connection and harmonious relationships.',
      features: [
        'Couples counseling',
        'Relationship healing',
        'Communication mastery',
        'Sacred partnership'
      ]
    },
    {
      icon: <Moon size={32} />,
      title: 'Womb Healing & Feminine Embodiment',
      description: 'Specialized programs for women to reconnect with their feminine essence and sacred power.',
      features: [
        'Womb healing practices',
        'Feminine embodiment',
        'Cycle awareness',
        'Sacred feminine awakening'
      ]
    },
    {
      icon: <Eye size={32} />,
      title: 'Talks & Community Programs',
      description: 'Inspiring presentations and programs for communities, organizations, and special events.',
      features: [
        'Keynote speaking',
        'Panel discussions',
        'Community workshops',
        'Special event facilitation'
      ]
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="heading-hero">What I Offer</h1>
          <p className="body-large" style={{ maxWidth: '700px', margin: '1.5rem auto 0' }}>
            Holistic pathways to healing, embodiment, and soulful living
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-section">
        <div className="container">
          <div className="services-main-grid">
            {services.map((service, index) => (
              <div key={service.title} className="service-detail-card hover-lift">
                <div className="service-card-icon">{service.icon}</div>
                <h3 className="heading-2" style={{ marginBottom: '1rem' }}>{service.title}</h3>
                <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                  {service.description}
                </p>
                <ul className="service-features">
                  {service.features.map((feature) => (
                    <li key={feature} className="body-small">
                      <span className="feature-dot"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>How We Work Together</h2>
          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">01</div>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>Connect</h3>
              <p className="body-small">Reach out to discuss your needs and intentions for healing and growth.</p>
            </div>
            <div className="process-step">
              <div className="process-number">02</div>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>Align</h3>
              <p className="body-small">We'll create a personalized plan that honors your unique journey.</p>
            </div>
            <div className="process-step">
              <div className="process-number">03</div>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>Transform</h3>
              <p className="body-small">Experience healing, embodiment, and profound transformation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="services-gallery-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '2rem' }}>Experience the Journey</h2>
          <div className="services-gallery-grid">
            <img 
              src="https://customer-assets.emergentagent.com/job_33648ce9-55a6-4073-8c73-14e57d771a90/artifacts/nw40rq8t_WhatsApp%20Image%202026-04-16%20at%2012.49.13.jpeg" 
              alt="Healing session" 
              className="gallery-image"
              onClick={() => setSelectedImage('https://customer-assets.emergentagent.com/job_33648ce9-55a6-4073-8c73-14e57d771a90/artifacts/nw40rq8t_WhatsApp%20Image%202026-04-16%20at%2012.49.13.jpeg')}
              style={{ cursor: 'pointer' }}
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_pleasure-medicine/artifacts/dx5dz7ya_IMG_9484.jpeg" 
              alt="Workshop" 
              className="gallery-image"
              onClick={() => setSelectedImage('https://customer-assets.emergentagent.com/job_pleasure-medicine/artifacts/dx5dz7ya_IMG_9484.jpeg')}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="image-modal"
          onClick={() => setSelectedImage(null)}
        >
          <div className="image-modal-content">
            <button 
              className="image-modal-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close"
            >
              ×
            </button>
            <img src={selectedImage} alt="Full view" />
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <div className="services-cta-content">
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>Ready to Begin?</h2>
            <p className="body-large" style={{ marginBottom: '2rem' }}>
              Let's explore how these offerings can support your healing and transformation.
            </p>
            <Link to="/contact" className="btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};